import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Building2, Swords, ShieldCheck, Target, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { loggedGet } from './loggedFetch';
import { SNAPSHOT_QUERY_OPTIONS } from './cacheConfig';
import FreshnessBadge from './FreshnessBadge';
import ClassifierQualityBadge from './ClassifierQualityBadge';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatPct(p) {
  if (p == null) return '—';
  return `${(p * 100).toFixed(0)}%`;
}

function formatPctDecimal(p) {
  if (p == null) return '—';
  const pct = p * 100;
  if (pct < 1) return `${pct.toFixed(1)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
}

function SnapshotStrip({ vendor, includeLowConfidence, strict, productFamily, departmentIds, listName, years, onDrilldown, collapsed, onToggle }) {
  // The KPI strip sits directly above the trajectory bars, so its `years`
  // window has to use the same fiscal boundary or the totals won't add up to
  // the chart. No toggle here — this strip has no FY axis of its own.
  const { basis, isDefaultBasis } = useFiscalBasisContext();
  const qs = new URLSearchParams();
  if (includeLowConfidence) qs.set('includeLowConfidence', 'true');
  if (!strict) qs.set('strict', 'false');
  if (productFamily) qs.set('product_family', productFamily);
  // Phase H.29: department_ids filter for list scoping
  if (departmentIds?.length) qs.set('department_ids', departmentIds.join(','));
  // Global time filter from TimeFilterContext (null = all-time)
  if (years != null) qs.set('years', String(years));
  // Omit on the default basis: the request stays byte-identical to before.
  if (!isDefaultBasis) qs.set('basis', basis);
  const suffix = qs.toString() ? `?${qs}` : '';
  const path = `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/snapshot${suffix}`;
  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-analysis', 'snapshot', vendor, includeLowConfidence, strict, productFamily, departmentIds, years, basis],
    queryFn: loggedGet('SnapshotStrip:fetch', path),
    enabled: !!vendor,
    ...SNAPSHOT_QUERY_OPTIONS,
  });

  // Toggle button for collapsing/expanding (shown even when loading)
  const toggleButton = onToggle && (
    <button
      type="button"
      className="vca-snapshot-toggle"
      onClick={onToggle}
      title={collapsed ? 'Show KPI cards' : 'Hide KPI cards'}
    >
      {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      <span>{collapsed ? 'Show KPIs' : 'Hide'}</span>
    </button>
  );

  // If collapsed, show only the toggle button
  if (collapsed) {
    return <div className="vca-snapshot-collapsed">{toggleButton}</div>;
  }

  if (isLoading) return (
    <div className="vca-snapshot-wrapper">
      {toggleButton}
      <div className="vca-section__loading">Loading snapshot…</div>
    </div>
  );
  if (error) return (
    <div className="vca-snapshot-wrapper">
      {toggleButton}
      <div className="vca-section__error">Snapshot failed: {error.message}</div>
    </div>
  );
  if (!data) return toggleButton ? <div className="vca-snapshot-wrapper">{toggleButton}</div> : null;

  const dateRange = data.first_po_date && data.last_po_date
    ? `${new Date(data.first_po_date).getFullYear()}–${new Date(data.last_po_date).getFullYear()}`
    : '—';

  const trustPctLabel = data.trust?.high_conf_pct != null
    ? `${(data.trust.high_conf_pct * 100).toFixed(0)}%`
    : '—';

  // TAM = Total Addressable Market (state spend in categories where vendor competes)
  // Penetration = vendor spend / TAM
  const hasTam = data.tam != null && data.tam > 0;
  const penetrationPct = hasTam ? data.total_spend / data.tam : null;

  return (
    <div className="vca-snapshot-wrapper">
      {toggleButton}
      <div className="vca-snapshot vca-snapshot--compact">
        {/* Spend card */}
        <div
          className="vca-snapshot-card vca-snapshot-card--spend"
          onClick={() => onDrilldown({ type: 'vendor', value: vendor })}
          title="Drill into all classified spend"
        >
          <span className="vca-snapshot-card__label">
            <DollarSign size={12} /> Spend
            {data.data_freshness?.freshness_pct != null && (
              <FreshnessBadge
                freshnessPct={data.data_freshness.freshness_pct}
                mostRecentDate={data.data_freshness.most_recent_po_date}
                asOfQuery={data.data_freshness.as_of_query}
                compact
              />
            )}
          </span>
          <span className="vca-snapshot-card__value">{formatCurrency(data.total_spend)}</span>
          <span className="vca-snapshot-card__sub">
            {data.po_line_count?.toLocaleString()} items · {dateRange}
          </span>
        </div>

        {/* TAM card */}
        <div className="vca-snapshot-card" title="Total addressable market — state spend in shared categories">
          <span className="vca-snapshot-card__label">
            <Target size={12} /> TAM
          </span>
          <span className="vca-snapshot-card__value">
            {hasTam ? formatCurrency(data.tam) : '—'}
          </span>
          <span className="vca-snapshot-card__sub">
            {hasTam ? `${data.category_count || '—'} categories` : 'Calculating…'}
          </span>
        </div>

        {/* Penetration card */}
        <div className="vca-snapshot-card" title="Vendor spend as % of addressable market">
          <span className="vca-snapshot-card__label">
            Penetration
          </span>
          <span className="vca-snapshot-card__value">
            {hasTam ? formatPctDecimal(penetrationPct) : '—'}
          </span>
          <span className="vca-snapshot-card__sub">of addressable market</span>
        </div>

        {/* Agencies card */}
        <div className="vca-snapshot-card">
          <span className="vca-snapshot-card__label">
            <Building2 size={12} /> Agencies
          </span>
          <span className="vca-snapshot-card__value">{data.agency_count?.toLocaleString()}</span>
          <span className="vca-snapshot-card__sub">with active spend</span>
        </div>

        {/* Competitors card */}
        <div className="vca-snapshot-card">
          <span className="vca-snapshot-card__label">
            <Swords size={12} /> Competitors
          </span>
          <span className="vca-snapshot-card__value">{data.competitor_count?.toLocaleString()}</span>
          <span className="vca-snapshot-card__sub">in shared categories</span>
        </div>

        {/* Trust Score card */}
        <div className="vca-snapshot-card" title={`High-confidence rows ≥ ${((data.trust?.confidence_floor ?? 0.75) * 100).toFixed(0)}%`}>
          <span className="vca-snapshot-card__label">
            <ShieldCheck size={12} /> Trust
            {data.classifier_quality?.strict_vs_raw_ratio != null && (
              <ClassifierQualityBadge
                strictVsRawRatio={data.classifier_quality.strict_vs_raw_ratio}
                rawCount={data.classifier_quality.raw_count}
                strictCount={data.classifier_quality.strict_count}
                compact
              />
            )}
          </span>
          <span className="vca-snapshot-card__value">{trustPctLabel}</span>
          <span className="vca-snapshot-card__sub">
            {data.trust?.high_conf_count?.toLocaleString() || 0} hi / {data.trust?.low_conf_count?.toLocaleString() || 0} lo
          </span>
        </div>
      </div>
    </div>
  );
}

export default SnapshotStrip;
